import { useEffect, useState } from "react";
import { X, PhoneCall, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const timeSlots = [
  "Tomorrow 10am PT",
  "Tomorrow 2pm PT",
  "This week — flexible",
  "Next week — flexible",
  "Custom (specify in notes)",
];

export default function BookStrategyCallModal({ open, onClose, svc }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [preferred, setPreferred] = useState(timeSlots[0]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setCompany(user?.company || "");
      setPreferred(timeSlots[0]);
      setNotes("");
      setBusy(false);
      setDone(false);
    }
  }, [open, user]);

  if (!open) return null;

  const topic = svc ? `${svc.name} — ${svc.categoryLabel}` : "General strategy call";

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("ERROR · name and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/bookings", {
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || null,
        preferred_time: preferred,
        topic,
        notes: notes.trim() || null,
      });
      setDone(true);
      toast.success("OK · booking received · we'll confirm within 24h");
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Booking failed";
      toast.error(`ERROR · ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="book-call-modal"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#0B0C10]/80 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md bg-[#0d1117] border border-[#1F2833] shadow-2xl">
        <div className="border-b border-[#1F2833] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
              // STRATEGY.CALL
            </div>
            <div className="mt-1 font-display text-lg font-bold tracking-tight text-[#F5F5F7]">
              Book a call
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="modal-close"
            className="p-2 text-[#C5C6C7] hover:text-[#FF3355] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 border-2 border-emerald-400 bg-emerald-400/10 mb-4">
              <Check size={24} className="text-emerald-400" />
            </div>
            <div className="font-display text-xl font-bold text-[#F5F5F7]">
              Signal received.
            </div>
            <p className="mt-2 text-sm text-[#C5C6C7]">
              A co-founder will confirm within 24 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="book-call-form">
            <div className="border border-[#1F2833] bg-[#0B0C10] p-3 font-mono text-[11px] text-[#C5C6C7]">
              <span className="text-[#FF3355]">&gt;</span> topic: {topic}
            </div>

            <FieldRow label="your name *">
              <input
                data-testid="booking-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-2.5 font-mono text-sm text-[#F5F5F7]"
              />
            </FieldRow>

            <FieldRow label="email *">
              <input
                data-testid="booking-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-2.5 font-mono text-sm text-[#F5F5F7]"
              />
            </FieldRow>

            <FieldRow label="company (optional)">
              <input
                data-testid="booking-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-2.5 font-mono text-sm text-[#F5F5F7]"
              />
            </FieldRow>

            <FieldRow label="preferred time">
              <select
                data-testid="booking-time"
                value={preferred}
                onChange={(e) => setPreferred(e.target.value)}
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-2.5 font-mono text-sm text-[#F5F5F7]"
              >
                {timeSlots.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </FieldRow>

            <FieldRow label="notes (optional)">
              <textarea
                data-testid="booking-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything we should know before the call?"
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none p-3 font-mono text-sm text-[#F5F5F7] resize-y"
              />
            </FieldRow>

            <button
              type="submit"
              disabled={busy}
              data-testid="booking-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-400 text-[#0B0C10] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-colors disabled:opacity-40"
            >
              {busy ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  <PhoneCall size={13} /> Request call
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-1.5">
        &gt; {label}
      </div>
      {children}
    </div>
  );
}
